import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

const ISLANDS = ["Viti Levu", "Vanua Levu", "Mamanuca Islands", "Yasawa Islands", "Kadavu", "Taveuni"];

export default function JVSubmit() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [form, setForm] = useState({
        title: "",
        project_vision: "",
        project_type: "tourism",
        capital_required_fjd: 1000000,
        landowner_equity_pct: 22,
        timeline_months: 24,
        roi_estimate_pct: 18,
        risk_profile: "medium",
        financial_projections: "",
        location_island: "Viti Levu",
        location_region: "",
    });
    const [loading, setLoading] = useState(false);

    const update = (k, v) => setForm({ ...form, [k]: v });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/jv", {
                ...form,
                capital_required_fjd: Number(form.capital_required_fjd),
                landowner_equity_pct: Number(form.landowner_equity_pct),
                timeline_months: parseInt(form.timeline_months),
                roi_estimate_pct: Number(form.roi_estimate_pct),
            });
            toast.success("Joint Venture submitted! Our deal team will review it shortly.");
            navigate("/dashboard");
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (detail === "phone_verification_required") {
                toast.error("Please verify your phone number first.");
                navigate("/verify-phone");
                return;
            }
            toast.error(formatApiError(detail));
        } finally {
            setLoading(false);
        }
    };

    if (user && !user.roles?.some(r => ["seller", "agent", "admin"].includes(r))) {
        return (
            <div className="max-w-2xl mx-auto px-4 py-20 text-center">
                <Sparkles className="h-12 w-12 mx-auto text-coral mb-4" />
                <h1 className="font-display text-3xl font-bold mb-3">Only sellers & agents can submit Joint Venture proposals</h1>
                <p className="text-muted-foreground font-app mb-6">Add the seller or agent role to your account to continue.</p>
                <Button onClick={() => navigate("/dashboard")}>Go to dashboard</Button>
            </div>
        );
    }

    return (
        <div data-testid="jv-submit-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-8">
                <div className="text-xs uppercase tracking-[0.3em] text-coral font-app font-semibold mb-2">Joint Venture Submission</div>
                <h1 className="font-display text-4xl font-bold mb-3">Submit a Joint Venture proposal</h1>
                <p className="font-app text-muted-foreground max-w-2xl">Your proposal goes into a private Pending Review queue visible only to Bula Real Estate Fiji admins. We score, vet, and either approve, request more info, or decline before any investor sees it.</p>
            </div>

            <Card className="p-8">
                <form onSubmit={submit} className="space-y-6">
                    <div>
                        <Label>Project title</Label>
                        <Input data-testid="jv-title-input" required value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="e.g. Coral Coast Eco-Resort Joint Venture" className="mt-1.5" />
                    </div>
                    <div>
                        <Label>Project vision</Label>
                        <Textarea data-testid="jv-vision-input" required value={form.project_vision} onChange={(e) => update("project_vision", e.target.value)} rows={5} placeholder="Describe what you want to build, who it's for, and the landowner's role..." className="mt-1.5" />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Project type</Label>
                            <Select value={form.project_type} onValueChange={(v) => update("project_type", v)}>
                                <SelectTrigger data-testid="jv-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="tourism">Tourism</SelectItem>
                                    <SelectItem value="residential">Residential</SelectItem>
                                    <SelectItem value="commercial">Commercial</SelectItem>
                                    <SelectItem value="subdivision">Subdivision</SelectItem>
                                    <SelectItem value="mixed_use">Mixed Use</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Risk profile</Label>
                            <Select value={form.risk_profile} onValueChange={(v) => update("risk_profile", v)}>
                                <SelectTrigger data-testid="jv-risk-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">Low</SelectItem>
                                    <SelectItem value="medium">Medium</SelectItem>
                                    <SelectItem value="high">High</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Capital required (FJD)</Label>
                            <Input data-testid="jv-capital-input" type="number" required value={form.capital_required_fjd} onChange={(e) => update("capital_required_fjd", e.target.value)} className="mt-1.5" />
                        </div>
                        <div>
                            <Label>Landowner equity (%)</Label>
                            <Input data-testid="jv-equity-input" type="number" step="0.1" required value={form.landowner_equity_pct} onChange={(e) => update("landowner_equity_pct", e.target.value)} className="mt-1.5" />
                        </div>
                        <div>
                            <Label>Timeline (months)</Label>
                            <Input data-testid="jv-timeline-input" type="number" required value={form.timeline_months} onChange={(e) => update("timeline_months", e.target.value)} className="mt-1.5" />
                        </div>
                        <div>
                            <Label>ROI estimate (%)</Label>
                            <Input data-testid="jv-roi-input" type="number" step="0.1" required value={form.roi_estimate_pct} onChange={(e) => update("roi_estimate_pct", e.target.value)} className="mt-1.5" />
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Island</Label>
                            <Select value={form.location_island} onValueChange={(v) => update("location_island", v)}>
                                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {ISLANDS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Region</Label>
                            <Input data-testid="jv-region-input" required value={form.location_region} onChange={(e) => update("location_region", e.target.value)} placeholder="Coral Coast, Savusavu..." className="mt-1.5" />
                        </div>
                    </div>

                    <div>
                        <Label>Financial projections</Label>
                        <Textarea data-testid="jv-financials-input" required value={form.financial_projections} onChange={(e) => update("financial_projections", e.target.value)} rows={5} placeholder="Revenue, costs, exit strategy..." className="mt-1.5" />
                    </div>

                    <div className="bg-coral/10 border border-coral/30 rounded-md p-4 flex items-start gap-3">
                        <ShieldCheck className="h-5 w-5 text-coral flex-shrink-0 mt-0.5" />
                        <p className="text-sm font-app text-muted-foreground">
                            Your submission stays private until our admin team reviews and approves it. We may request more information before publishing.
                        </p>
                    </div>

                    <Button type="submit" data-testid="jv-submit-btn" disabled={loading} className="w-full bg-ocean-green text-white hover:opacity-90 font-app h-12">
                        {loading ? "Submitting…" : <>Submit for review <ArrowRight className="ml-2 h-4 w-4" /></>}
                    </Button>
                </form>
            </Card>
        </div>
    );
}
